import { Component, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Domain } from '../../../shared/classes/domain';
import { Errors, FormValidationService } from '../../../shared/forms';
import { LoginService } from '../../../shared/services/login.service';
import { CrudService } from '../../crud.service';
import { DomainVerificationType } from '../../../shared/enums/domain-verification-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { DomainErrorCode } from '../../../shared/enums/domain-error-code.enum';
import { HttpStatus } from '../../../shared/enums/http-status.enum';
import { firstValueFrom } from 'rxjs';

export enum CreateDomainError {
  UnexpectedFailure = 'UNEXPECTED_FAILURE',
  DomainInUse = 'DOMAIN_IN_USE',
  DomainBlocked = 'DOMAIN_BLOCKED'
}

@Component({
  templateUrl: './create-domain-dialog.component.html',
  styleUrls: ['./create-domain-dialog.component.scss'],
  standalone: false
})
export class CreateDomainDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<CreateDomainDialogComponent>>(MatDialogRef);
  private readonly loginService = inject(LoginService);
  private readonly crudService = inject(CrudService);
  private readonly validation = inject(FormValidationService);

  public readonly CreateDomainError = CreateDomainError;

  public readonly domainErrors: Errors = {
    'required': 'Domain is required',
    'pattern': 'Must be a valid domain',
  };

  public busy = false;
  public error: CreateDomainError;

  public readonly domainForm = new FormGroup({
    domainName: this.validation.getDomainControl(),
  });
  public get domainName() { return this.domainForm.get('domainName'); }

  public onCancel(): void {
    this.dialogRef.close(null);
  }

  public async onSubmit(): Promise<void> {
    this.busy = true;
    this.error = null;

    const domain = new Domain();
    domain.domain = this.domainName.value.trim();
    domain.verificationType = DomainVerificationType.Dns;

    try {
      const enterpriseId = this.loginService.user.enterpriseId;
      const createdDomain = await firstValueFrom(this.crudService.createDomain(enterpriseId, domain), { defaultValue: undefined });
      this.dialogRef.close(createdDomain);
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === HttpStatus.BadRequest && e.error.error_code === DomainErrorCode.DomainBlocked) {
          this.error = CreateDomainError.DomainBlocked;
        } else if (e.status === HttpStatus.Conflict && e.error.error_code === DomainErrorCode.DomainInUse) {
          this.error = CreateDomainError.DomainInUse;
        } else {
          this.error = CreateDomainError.UnexpectedFailure;
        }
      } else {
        this.error = CreateDomainError.UnexpectedFailure;
      }
    } finally {
      this.busy = false;
    }
  }
}
